import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
  skyAuthoredPoseContact,
} from './115-sky-authored-pose.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';
const ANCHORS = [
  'Anchor_port_lower',
  'Anchor_port_upper',
  'Anchor_starboard_lower',
  'Anchor_starboard_upper',
];
const axes = ['x', 'y', 'z'];
const clamp = (v, low, high) => Math.max(low, Math.min(high, v));
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
const point = (values, offset = 0) => ({
  x: values[offset],
  y: values[offset + 1],
  z: values[offset + 2],
});
const lerp = (a, b, t) =>
  Object.fromEntries(axes.map((axis) => [axis, a[axis] + (b[axis] - a[axis]) * t]));
const translate = (a, b, sign = 1) =>
  Object.fromEntries(axes.map((axis) => [axis, a[axis] + b[axis] * sign]));
const identity = (position = [0, 0, 0]) => ({
  position,
  quaternion: [0, 0, 0, 1],
  scale: [1, 1, 1],
});

/** Require original metre-scale graveyard and phantom rigs at readiness. Each
 * role validates named source articulation; missing assets never unlock a fight. */
export function installSkyMourningBrideRig(state, source) {
  const rig = compileSkyAuthoredRig(source),
    role = rig?.worldId;
  if (!state || !['mourning-bride-wreck', 'mourning-bride-echo'].includes(role)) return false;
  const names = role.endsWith('wreck') ? ['SearchlightSweep'] : ['Flight', 'Launch', 'Death'];
  if (!names.every((name) => rig.clips.some((clip) => clip.name === name))) return false;
  if (
    role.endsWith('wreck') &&
    !['Lamp_starboard_face', 'Lamp_port_face'].every((name) =>
      rig.sockets.some((s) => s.name === name),
    )
  )
    return false;
  state.authoredPropRigs ??= {};
  state.authoredPropRigs[role] = rig;
  return true;
}
function body(item) {
  const b = item.authoredPose.bounds;
  item.body = {
    x: (b.min[0] + b.max[0]) / 2,
    y: (b.min[1] + b.max[1]) / 2,
    z: b.max[2],
    radius: Math.max(...b.max.map((n, i) => n - b.min[i])) / 2,
  };
}

function lampTarget(prop, observer = null) {
  const face = prop.authoredPose.sockets.Lamp_starboard_face;
  if (!prop.lampTriangles) {
    prop.lampTriangles = [];
    const driver = prop.authoredRig.nodes.findIndex((node) => node.name === 'Lamp_starboard_pitch');
    prop.authoredRig.meshes.forEach((mesh, mi) => {
      const joint = mesh.jointNodes?.indexOf(driver);
      if (!(joint >= 0)) return;
      for (let i = 0; i < mesh.indices.length; i += 3) {
        const head = mesh.indices.slice(i, i + 3).every((vertex) => {
          let weight = 0;
          for (let slot = vertex * 4; slot < vertex * 4 + 4; slot++)
            if (mesh.joints[slot] === joint) weight += mesh.weights[slot];
          return weight > 0.999;
        });
        if (head) prop.lampTriangles.push([mi, i]);
      }
    });
  }
  let nearest = face,
    best = Infinity;
  for (const [mi, i] of prop.lampTriangles) {
    const p = centroid(prop.authoredPose.meshes[mi], i),
      d = distance(p, observer ?? face);
    if (d < best) {
      nearest = p;
      best = d;
    }
  }
  // The lamp face socket is inside its open reflector. Aim at an actual nearby
  // source triangle so turning the lamp cannot leave its controls in empty air.
  prop.weakpoint = { ...prop.weakpoint, ...nearest };
  prop.weakpoints = [prop.weakpoint];
}
function centroid(mesh, index) {
  const p = { x: 0, y: 0, z: 0 };
  for (let k = 0; k < 3; k++)
    for (let a = 0; a < 3; a++) p[axes[a]] += mesh.vertices[mesh.indices[index + k] * 3 + a] / 3;
  return p;
}

/** One original110m wreck occupies the fixed graveyard arena. Its starboard
 * lamp faces the near orbital arc, with real clearance for an inward shot.
 * Anchor health is persistent, and ordinary hull shots cannot bypass light. */
export function createSkyMourningBride(state, boss) {
  const wreck = state.authoredPropRigs?.['mourning-bride-wreck'],
    echo = state.authoredPropRigs?.['mourning-bride-echo'];
  if (boss.worldId !== 'mourning-bride' || !wreck || !echo) return false;
  const disabled = sampleSkyAuthoredPose(boss.authoredRig, 'AnchorDisabled', 3);
  boss.mourning = {
    age: 0,
    phase: 'approach',
    record: [],
    recordAge: 0,
    cycle: -1,
    echoes: [],
    beams: [],
    overrides: [],
    crashes: 0,
    anchors: ANCHORS.map((name, index) => ({
      name,
      index,
      hp: boss.maxHp / 4,
      maxHp: boss.maxHp / 4,
      lit: false,
      mesh: boss.authoredRig.meshes.findIndex((m) => m.name === name),
      pivot: boss.authoredRig.nodes.findIndex((n) => n.name === `${name}_pivot`),
    })),
    disabledPose: disabled.nodes,
  };
  const prop = {
    id: state.nextId++,
    kind: 'authored-prop',
    artVariant: wreck.worldId,
    worldId: wreck.worldId,
    authoredRig: wreck,
    rootTransform: identity([-18, 0, -32]),
    hp: 1,
    maxHp: 1,
    mourningLamp: true,
    collisionEnabled: true,
    selection: -1,
    activeUntil: 0,
    cooldown: 0,
    age: 0,
  };
  prop.authoredPose = sampleSkyAuthoredPose(wreck, null, 0, prop.rootTransform);
  prop.weakpoint = {
    ...prop.authoredPose.sockets.Lamp_starboard_face,
    id: 'Lamp_starboard_face',
    radius: 4,
    criticalRadius: 2,
    exposed: true,
  };
  lampTarget(prop, { x: state.x, y: state.y, z: state.pilotZ ?? 1 });
  body(prop);
  Object.assign(prop, point(prop.rootTransform.position));
  boss.authoredProps = [prop];
  boss.authoredFlightCeiling = 72;
  // A direct arena start begins beside the original carrier's vertical centre.
  // Full journeys retain the altitude actually flown; this never alters source
  // scale, orbit radius, anchor health or earned approach rewards.
  boss.mourning.entryHeight =
    (boss.authoredPose.bounds.min[1] + boss.authoredPose.bounds.max[1]) / 2;
  if (state.startMode === 'boss') {
    state.y = boss.mourning.entryHeight;
    state.locomotion = 'fly';
  }
  return true;
}
function aimRotation(from, to) {
  const a = Math.hypot(...from),
    b = Math.hypot(...to),
    f = from.map((n) => n / a),
    t = to.map((n) => n / b);
  const dot = f.reduce((sum, n, i) => sum + n * t[i], 0);
  if (dot < -0.999999) return [0, 0, 1, 0];
  const q = [
      f[1] * t[2] - f[2] * t[1],
      f[2] * t[0] - f[0] * t[2],
      f[0] * t[1] - f[1] * t[0],
      1 + dot,
    ],
    length = Math.hypot(...q);
  return q.map((n) => n / length);
}
function mixRotation(from, to, t) {
  const sign = from.reduce((sum, n, i) => sum + n * to[i], 0) < 0 ? -1 : 1;
  const q = from.map((n, i) => n * (1 - t) + to[i] * sign * t),
    length = Math.hypot(...q);
  return q.map((n) => n / length);
}
function directLamp(prop, target, dt, observer) {
  const rig = prop.authoredRig,
    rest = (prop.restPose ??= sampleSkyAuthoredPose(rig, null, 0, prop.rootTransform));
  const pitch = rig.nodes.findIndex((n) => n.name === 'Lamp_starboard_pitch'),
    face = rig.nodes.find((n) => n.name === 'Lamp_starboard_face');
  const parent = rest.matrices[rig.nodes[pitch].parent],
    pivot = point(rest.matrices[pitch], 12),
    direction = translate(target, pivot, -1);
  // Source parents carry the Blender-to-Y-up rotation. Transpose their rigid
  // rotation to solve the actual lamp bone locally, rather than aim a fake cone.
  const local = [0, 1, 2].map(
    (i) =>
      parent[i * 4] * direction.x +
      parent[i * 4 + 1] * direction.y +
      parent[i * 4 + 2] * direction.z,
  );
  const desired = aimRotation(face.position, local),
    prior = prop.lampRotation ?? rig.nodes[pitch].quaternion;
  prop.lampRotation = mixRotation(prior, desired, Math.min(1, dt * 3));
  prop.authoredPose = sampleSkyAuthoredPose(rig, null, 0, prop.rootTransform, {
    overrides: [
      {
        node: pitch,
        position: rig.nodes[pitch].position,
        quaternion: prop.lampRotation,
        scale: rig.nodes[pitch].scale,
      },
    ],
  });
  prop.weakpoint = { ...prop.weakpoint, ...prop.authoredPose.sockets.Lamp_starboard_face };
  lampTarget(prop, observer);
  body(prop);
  const from = prop.authoredPose.sockets.Lamp_starboard_face,
    center = point(prop.authoredPose.matrices[pitch], 12),
    delta = translate(from, center, -1),
    length = Math.hypot(delta.x, delta.y, delta.z);
  return {
    from: { ...from },
    to: translate(
      from,
      Object.fromEntries(axes.map((axis) => [axis, (delta[axis] / length) * 160])),
    ),
  };
}
function damageAnchor(state, anchor, amount, event, source) {
  const boss = state.boss,
    systems = boss.mourning,
    removed = Math.min(anchor.hp, Math.max(0, amount));
  anchor.hp -= removed;
  boss.hp = Math.max(
    0,
    systems.anchors.reduce((sum, a) => sum + a.hp, 0),
  );
  if (anchor.hp === 0 && removed > 0) {
    const transform = systems.disabledPose[anchor.pivot];
    systems.overrides.push({ node: anchor.pivot, ...structuredClone(transform) });
    event(
      state,
      'bride-anchor-disabled',
      `${anchor.name.includes('port') ? 'PORT' : 'STARBOARD'} ANCHOR DISABLED`,
    );
  }
  if (source === 'echo' && removed > 0) systems.crashes++;
  return removed;
}
function spawnEcho(state) {
  const boss = state.boss,
    systems = boss.mourning,
    rig = state.authoredPropRigs['mourning-bride-echo'];
  const path = systems.record.map((p) => ({ ...p }));
  if (path.length < 2 || systems.echoes.length >= 2) return;
  const launch = { ...boss.authoredPose.sockets[`Echo_launch_${systems.cycle % 3}`] };
  const echo = {
    id: state.nextId++,
    kind: 'authored-prop',
    artVariant: rig.worldId,
    worldId: rig.worldId,
    authoredRig: rig,
    mourningEcho: true,
    hp: 3,
    maxHp: 3,
    age: 0,
    path,
    launch,
    collisionEnabled: true,
    rootTransform: identity(),
    authoredClip: 'Launch',
    x: launch.x,
    y: launch.y,
    z: launch.z,
  };
  systems.echoes.push(echo);
  boss.authoredProps.push(echo);
}

/** Record only simulation-time player samples. Every eight seconds the carrier
 * tells for1.5s, then emits an original phantom following its own frozen copy of
 * the last four seconds of actual player XYZ. Changing the live path cannot retarget it. */
export function stepSkyMourningBride(state, dt, { event }) {
  const boss = state.boss,
    s = boss.mourning;
  s.age += dt;
  s.recordAge += dt;
  if (s.recordAge >= 0.05) {
    s.recordAge %= 0.05;
    s.record.push({ x: state.x, y: state.y, z: state.pilotZ ?? 1 });
    if (s.record.length > 81) s.record.shift();
  }
  const active = Math.max(0, s.age - 4),
    cycle = Math.floor(active / 8),
    age = active % 8;
  s.phase = s.age < 4 ? 'approach' : age < 1.5 ? 'recording' : 'pursuit';
  if (s.age >= 4 && age >= 1.5 && cycle !== s.cycle) {
    s.cycle = cycle;
    spawnEcho(state);
    event(state, 'bride-echo', 'ECHO REPLAY · CHANGE YOUR FLIGHT PATH');
  }
  for (const prop of boss.authoredProps)
    if (prop.mourningLamp) {
      prop.age += dt;
      prop.cooldown = Math.max(0, prop.cooldown - dt);
    }
  const remaining = s.anchors.filter((a) => a.hp > 0).length;
  boss.controlHint =
    s.age < 4
      ? 'A/D ORBIT · W/S CLIMB · SHOOT THE SEARCHLIGHT'
      : s.anchors.some((a) => a.lit && a.hp > 0)
        ? `ORBIT / CLIMB · FIRE AT SOLID ANCHOR · ${remaining} REMAIN`
        : 'ORBIT TO THE WRECK LAMP · SHOOT TO CYCLE';
  return {
    clip:
      s.phase === 'recording'
        ? 'EchoLaunch'
        : s.anchors.some((a) => a.lit)
          ? 'AnchorReveal'
          : 'SpectralGlide',
    seconds:
      s.phase === 'recording' ? age : s.revealAge > 0 ? Math.min(3, s.revealAge) : active % 8,
    exposed: false,
    target: ANCHORS[s.anchors.findIndex((a) => a.hp > 0)] + '_target',
  };
}
function overlapping(a, b) {
  return axes.every((_, i) => a.min[i] <= b.max[i] && a.max[i] >= b.min[i]);
}
function echoStep(state, echo, dt, { event, hit }) {
  const boss = state.boss,
    s = boss.mourning,
    previous = echo.authoredPose;
  echo.age += dt;
  if (echo.hp <= 0) {
    echo.deathAge = (echo.deathAge ?? 0) + dt;
    echo.collisionEnabled = false;
    echo.authoredClip = 'Death';
    echo.authoredPose = sampleSkyAuthoredPose(
      echo.authoredRig,
      'Death',
      echo.deathAge,
      echo.rootTransform,
    );
    return;
  }
  const start = echo.path[0],
    play = clamp((echo.age - 1) / 4, 0, 1),
    index = play * (echo.path.length - 1),
    i = Math.floor(index);
  const p =
    echo.age < 1
      ? lerp(echo.launch, start, echo.age)
      : lerp(echo.path[i], echo.path[Math.min(i + 1, echo.path.length - 1)], index - i);
  const prior = echo.previousOrigin ?? echo.launch;
  const yaw = Math.atan2(-(p.x - prior.x), -(p.z - prior.z));
  if (Math.hypot(p.x - prior.x, p.z - prior.z) > 1e-6) echo.yaw = yaw;
  Object.assign(echo, p);
  echo.rootTransform = {
    position: [p.x, p.y, p.z],
    quaternion: [0, Math.sin((echo.yaw ?? 0) / 2), 0, Math.cos((echo.yaw ?? 0) / 2)],
    scale: [1, 1, 1],
  };
  echo.authoredClip = echo.age < 1 ? 'Launch' : 'Flight';
  echo.authoredPose = sampleSkyAuthoredPose(
    echo.authoredRig,
    echo.authoredClip,
    echo.age % 3,
    echo.rootTransform,
  );
  body(echo);
  const solid = s.anchors.filter((a) => a.hp > 0 && a.lit);
  if (previous && echo.age >= 1 && solid.length) {
    const swept = {
      min: echo.authoredPose.bounds.min.map((v, i) => Math.min(v, previous.bounds.min[i])),
      max: echo.authoredPose.bounds.max.map((v, i) => Math.max(v, previous.bounds.max[i])),
    };
    const nearby = solid.filter((a) => overlapping(swept, boss.authoredPose.meshes[a.mesh].bounds));
    if (nearby.length)
      for (let mi = 0; mi < echo.authoredPose.meshes.length && echo.hp > 0; mi++) {
        const mesh = echo.authoredPose.meshes[mi],
          before = previous.meshes[mi];
        for (let v = 0; v < mesh.vertices.length; v += 3) {
          const contact = skyAuthoredPoseContact(
            boss.authoredPose,
            point(before.vertices, v),
            point(mesh.vertices, v),
            nearby.map((a) => a.mesh),
          );
          if (contact) {
            const a = nearby.find((a) => a.mesh === contact.meshIndex);
            damageAnchor(state, a, a.maxHp * 0.65, event, 'echo');
            echo.hp = 0;
            echo.crash = { ...contact };
            event(state, 'bride-echo-crash', 'ECHO SHATTERED ON A SOLID ANCHOR');
            break;
          }
        }
      }
  }
  if (echo.hp > 0 && echo.age >= 1 && Math.abs(echo.z - (state.pilotZ ?? 1)) < 4) {
    const pilot = { x: state.x, y: state.y, z: state.pilotZ ?? 1 };
    const drift = previous
      ? translate(
          point(echo.rootTransform.position),
          echo.previousOrigin ?? point(echo.rootTransform.position),
          -1,
        )
      : { x: 0, y: 0, z: 0 };
    const contact = skyAuthoredPoseContact(
      echo.authoredPose,
      translate(s.previousPilot ?? pilot, drift),
      pilot,
      null,
      SKY_WORLD.pilotContactRadius,
    );
    if (contact) {
      hit?.(state, 'phantom-echo');
      echo.hp = 0;
    }
  }
  echo.previousOrigin = { ...p };
}

/** After116 applies the shared boss pose, the real articulated lamp ray pins
 * only the first physical anchor it reaches. Echo/anchor hits use moving source
 * vertices against posed anchor triangles; spectral sections cannot cause hits. */
export function finishSkyMourningBride(state, dt, { event, hit }) {
  const boss = state.boss,
    s = boss.mourning;
  s.beams = [];
  for (const a of s.anchors) a.lit = false;
  for (const prop of boss.authoredProps)
    if (prop.mourningLamp && prop.selection >= 0) {
      const a = s.anchors[prop.selection],
        target = boss.authoredPose.sockets[`${a.name}_target`];
      const beam = directLamp(prop, target, dt, { x: state.x, y: state.y, z: state.pilotZ ?? 1 });
      if (prop.activeUntil > s.age && a.hp > 0) {
        const contact = skyAuthoredPoseContact(
          boss.authoredPose,
          beam.from,
          beam.to,
          s.anchors.filter((a) => a.hp > 0).map((a) => a.mesh),
        );
        if (contact) {
          const selected = s.anchors.find((a) => a.mesh === contact.meshIndex);
          selected.lit = true;
          selected.surface = { x: contact.x, y: contact.y, z: contact.z };
          beam.to = { x: contact.x, y: contact.y, z: contact.z };
        }
        s.beams.push(beam);
      }
    }
  for (const echo of s.echoes) echoStep(state, echo, dt, { event, hit });
  s.echoes = s.echoes.filter((e) => (e.hp > 0 ? e.age < 5.5 : (e.deathAge ?? 0) < 2.583));
  s.previousPilot = { x: state.x, y: state.y, z: state.pilotZ ?? 1 };
  boss.authoredProps = boss.authoredProps.filter((p) => !p.mourningEcho || s.echoes.includes(p));
  boss.weakpoints = s.anchors
    .filter((a) => a.hp > 0 && a.lit)
    .map((a) => ({
      ...(a.surface ?? boss.authoredPose.sockets[`${a.name}_target`]),
      id: a.name,
      radius: 3,
      criticalRadius: 2,
      exposed: true,
    }));
  boss.weakpoint = boss.weakpoints[0] ?? {
    ...boss.authoredPose.sockets.Anchor_port_lower_target,
    radius: 2,
    exposed: false,
  };
  boss.anatomy.targets = boss.weakpoints;
  boss.vulnerable = boss.weakpoints.length > 0;
  boss.brideMaterialStates = Object.fromEntries(
    boss.authoredRig.meshes.map((m) => {
      const a = s.anchors.find((a) => a.name === m.name);
      return [
        m.node,
        a
          ? { opacity: a.hp <= 0 ? 0.45 : a.lit ? 1 : 0.18, lit: a.lit, damaged: a.hp <= 0 }
          : { opacity: 0.22, lit: false, damaged: false },
      ];
    }),
  );
}

/** Unlit carrier sections are truly spectral for weapon contact. Only the
 * illuminated original anchor triangles can receive an impact; no socket orb. */
export function skyMourningBrideContact(boss, from, to) {
  const meshes = boss.mourning.anchors.filter((a) => a.lit && a.hp > 0).map((a) => a.mesh);
  const contact = skyAuthoredPoseContact(boss.authoredPose, from, to, meshes);
  return contact ? { ...contact, region: 'core', authoredCritical: true } : null;
}

/** Manual lamp hits activate for16s and select the next intact anchor. Each
 * command has a.65s physical debounce, so held fire remains controllable. Echoes
 * may be shot defensively; anchor loss persists and alone drives guardian HP. */
export function resolveSkyMourningBrideContact(
  state,
  target,
  shot,
  contact,
  { damage },
  { event },
) {
  const boss = state.boss,
    s = boss?.mourning;
  if (!s) return undefined;
  if (target.mourningLamp) {
    if (
      shot.source !== 'drone' &&
      target.cooldown <= 0 &&
      target.lampTriangles.some(
        ([mesh, triangle]) => mesh === contact.meshIndex && triangle / 3 === contact.triangleIndex,
      )
    ) {
      const intact = s.anchors.filter((a) => a.hp > 0);
      const next = intact.find((a) => a.index > target.selection) ?? intact[0];
      if (next) {
        target.selection = next.index;
        target.activeUntil = s.age + 16;
        target.cooldown = 0.65;
        event(state, 'bride-light', 'SEARCHLIGHT MOTOR · PINNING ANCHOR');
      }
    }
    return { handled: true, damage: 0, quality: 'armor' };
  }
  if (target.mourningEcho) {
    const removed = Math.min(target.hp, Math.max(0, damage));
    target.hp -= removed;
    return { handled: true, damage: removed, quality: 'body', disabled: target.hp <= 0 };
  }
  if (target !== boss) return undefined;
  const anchor = s.anchors.find((a) => a.mesh === contact.meshIndex && a.lit && a.hp > 0);
  const removed =
    anchor && shot.source !== 'drone' ? damageAnchor(state, anchor, damage, event, 'weapon') : 0;
  return { handled: true, damage: removed, quality: removed ? 'critical' : 'armor' };
}
